import type { ScreenTour, ScreenTourMap, ScreenTourStep } from './types';

/** Orden de visitas del día: la misma pantalla en línea y «sin conexión» (cambia solo la bienvenida). */
const visitOrderSteps: ScreenTourStep[] = [
  { text: 'tours.visitOrders.office', target: 'visit-orders-office' },
  { text: 'tours.visitOrders.route', target: 'visit-orders-route' },
  { text: 'tours.visitOrders.newLoan', target: 'visit-orders-new-loan' },
  { text: 'tours.visitOrders.tabs', target: 'visit-orders-tabs' },
  { text: 'tours.visitOrders.cards', target: 'visit-orders-cards', whenMissing: 'center' },
  { text: 'tours.visitOrders.markPending', target: 'visit-orders-mark-pending' },
  { text: 'tours.visitOrders.pay', target: 'visit-orders-pay' },
  { text: 'tours.visitOrders.print', target: 'visit-orders-print' },
];

const visitOrders: ScreenTour = {
  key: 'screen-visit-orders',
  guide: 'orden-de-visitas',
  steps: [{ text: 'tours.visitOrders.welcome' }, ...visitOrderSteps],
};

/** Procesos: liquidación, orden de visitas, seguimiento, importar, exportar, transferir, auditoría y avisos. */
export const processTours: ScreenTourMap = {
  '/processes/route-settlement': {
    key: 'screen-route-settlement',
    guide: 'gastos-con-foto',
    steps: [
      { text: 'tours.routeSettlement.welcome' },
      { text: 'tours.routeSettlement.filters', target: 'route-settlement-filters' },
      { text: 'tours.routeSettlement.stats', target: 'route-settlement-stats', whenMissing: 'center' },
      { text: 'tours.routeSettlement.expenses', target: 'route-settlement-expenses' },
      { text: 'tours.routeSettlement.expenseActions', target: 'route-settlement-expenses-actions' },
      { text: 'tours.routeSettlement.registerExpense', target: 'route-settlement-register-expense' },
      { text: 'tours.routeSettlement.baseCash', target: 'route-settlement-base-cash' },
      { text: 'tours.routeSettlement.dayTotals', target: 'route-settlement-summary' },
      { text: 'tours.routeSettlement.routeIndicators', target: 'route-settlement-summary' },
      { text: 'tours.routeSettlement.expectedCash', target: 'route-settlement-summary' },
      { text: 'tours.routeSettlement.recalculate', target: 'route-settlement-recalculate' },
      { text: 'tours.routeSettlement.settle', target: 'route-settlement-settle' },
    ],
  },
  '/processes/visit-orders': visitOrders,
  '/processes/visit-orders/offline': {
    ...visitOrders,
    key: 'screen-visit-orders-offline',
    steps: [{ text: 'tours.visitOrdersOffline.welcome' }, ...visitOrderSteps],
  },
  '/processes/visit-order': {
    key: 'screen-visit-order-editor',
    guide: 'orden-de-visitas',
    steps: [
      { text: 'tours.visitOrderEditor.welcome' },
      { text: 'tours.visitOrderEditor.office', target: 'visit-order-editor-office' },
      { text: 'tours.visitOrderEditor.route', target: 'visit-order-editor-route' },
      { text: 'tours.visitOrderEditor.list', target: 'visit-order-editor-list' },
      { text: 'tours.visitOrderEditor.handle', target: 'visit-order-editor-handle' },
      { text: 'tours.visitOrderEditor.save', target: 'visit-order-editor-save' },
    ],
  },
  '/processes/route-tracking': {
    key: 'screen-route-tracking',
    guide: 'mapa-supervisores',
    steps: [
      { text: 'tours.routeTracking.welcome' },
      { text: 'tours.routeTracking.office', target: 'route-tracking-office' },
      { text: 'tours.routeTracking.route', target: 'route-tracking-route' },
      { text: 'tours.routeTracking.date', target: 'route-date' },
      { text: 'tours.routeTracking.query', target: 'route-tracking-query' },
      { text: 'tours.routeTracking.update', target: 'route-tracking-update' },
      { text: 'tours.routeTracking.meta', target: 'route-tracking-meta', whenMissing: 'center' },
      { text: 'tours.routeTracking.stats', target: 'route-tracking-stats', whenMissing: 'center' },
      { text: 'tours.routeTracking.view', target: 'route-view' },
      { text: 'tours.routeTracking.table', target: 'route-tracking' },
      { text: 'tours.routeTracking.flags', target: 'route-tracking-col-sync' },
      { text: 'tours.routeTracking.export', target: 'route-tracking-export' },
    ],
  },
  '/processes/export': {
    key: 'screen-client-export',
    steps: [
      { text: 'tours.clientExport.welcome' },
      { text: 'tours.clientExport.office', target: 'export-office' },
      { text: 'tours.clientExport.route', target: 'export-route' },
      { text: 'tours.clientExport.file', target: 'export-hint' },
      { text: 'tours.clientExport.export', target: 'export-csv' },
    ],
  },
  '/processes/import': {
    key: 'screen-loan-import',
    steps: [
      { text: 'tours.loanImport.welcome' },
      { text: 'tours.loanImport.history', target: 'import-history' },
      { text: 'tours.loanImport.office', target: 'import-office' },
      { text: 'tours.loanImport.route', target: 'import-route' },
      { text: 'tours.loanImport.loanType', target: 'import-loan-type' },
      { text: 'tours.loanImport.terms', target: 'import-terms' },
      { text: 'tours.loanImport.file', target: 'import-file' },
      { text: 'tours.loanImport.errors', target: 'import-errors' },
      { text: 'tours.loanImport.preview', target: 'import-preview' },
      { text: 'tours.loanImport.save', target: 'import-save' },
    ],
  },
  '/processes/imports': {
    key: 'screen-loan-imports',
    steps: [
      { text: 'tours.loanImports.welcome' },
      { text: 'tours.loanImports.goToImport', target: 'imports-go-to-import' },
      { text: 'tours.loanImports.deleteSelected', target: 'imports-delete-selected' },
      { text: 'tours.loanImports.office', target: 'imports-office' },
      { text: 'tours.loanImports.route', target: 'imports-route' },
      { text: 'tours.loanImports.query', target: 'imports-query' },
      { text: 'tours.loanImports.table', target: 'imports' },
      { text: 'tours.loanImports.status', target: 'imports-col-status' },
      { text: 'tours.loanImports.actions', target: 'imports-actions' },
      { text: 'tours.loanImports.export', target: 'imports-export' },
    ],
  },
  '/processes/loan-transfers': {
    key: 'screen-loan-transfers',
    steps: [
      { text: 'tours.loanTransfers.welcome' },
      { text: 'tours.loanTransfers.source', target: 'loan-transfers-source' },
      { text: 'tours.loanTransfers.target', target: 'loan-transfers-target' },
      { text: 'tours.loanTransfers.table', target: 'loan-transfers' },
      { text: 'tours.loanTransfers.select', target: 'loan-transfers-col-__select' },
      { text: 'tours.loanTransfers.transfer', target: 'loan-transfers-transfer' },
      { text: 'tours.loanTransfers.export', target: 'loan-transfers-export' },
    ],
  },
  '/processes/sync-audit': {
    key: 'screen-sync-audit',
    steps: [
      { text: 'tours.syncAudit.welcome' },
      { text: 'tours.syncAudit.office', target: 'sync-audit-office' },
      { text: 'tours.syncAudit.route', target: 'sync-audit-route' },
      { text: 'tours.syncAudit.status', target: 'sync-audit-status' },
      { text: 'tours.syncAudit.query', target: 'sync-audit-query' },
      { text: 'tours.syncAudit.table', target: 'sync-audit' },
      { text: 'tours.syncAudit.detail', target: 'sync-audit-actions' },
      { text: 'tours.syncAudit.export', target: 'sync-audit-export' },
    ],
  },
  '/processes/app-notices': {
    key: 'screen-app-notices',
    steps: [
      { text: 'tours.appNotices.welcome' },
      { text: 'tours.appNotices.title', target: 'app-notices-title' },
      { text: 'tours.appNotices.message', target: 'app-notices-message' },
      { text: 'tours.appNotices.seconds', target: 'app-notices-seconds' },
      { text: 'tours.appNotices.routes', target: 'app-notices' },
      { text: 'tours.appNotices.active', target: 'app-notices-col-alertActive' },
      { text: 'tours.appNotices.disable', target: 'app-notices-col-routeDisabled' },
      { text: 'tours.appNotices.save', target: 'app-notices-save' },
    ],
  },
};
